import React from 'react';
import { useContact } from '#modules/api/User';

const Image = ({ src, className, width, height, displayName }) => (
  <img
    src={src}
    className={className}
    width={width}
    height={height}
    title={displayName}
  />
);

/**
 * **`React Component`** Render an avatar icon for a paymail handle
 *
 * Props:
 * - `handle`: A paymail handle, for example "user.name@tkz.id"
 * - `contact`: Optional. If you already have TDisplayContact data, for example from
 *   one of the relationship list queries, you can pass it in and avoid the network
 *   request to look up the data for the handle. Note that the handle prop
 *   will be ignored if you supply a contact object.
 * - `counterparty`: Optional. As an alternative to looking up a handle, you can
 *   supply the counterparty object from an activity event, which includes contact
 *   information for counterparties that don’t have handles. Note that the handle prop
 *   will be ignored if you supply a counterparty object.
 * - `renderMissing`: Optional. Called when not yet loaded or in case of error
 * - `render`: Optional. Called with the display details, or if `renderMissing` is not supplied
 *
 * If an appropriate render prop is not supplied then an <img> will be rendered.
 *
 * Rendering props (passed to the render or renderMissing functions):
 *
 * - `isValid`: The definition of valid depends on the type of handle:
 *   - For Tokenized paymails: `valid` is `true` when the account exists.
 *   - For well-formed external paymails: `valid` is always `true`.
 *   - For well-formed BSV addresses: `valid` is always `true`.
 *   - `valid` is `false` in all other cases.
 * - `src`: The URL of an image to use as the avatar for the user
 * - `address`: The BSV address for the contact, if known.
 * - `displayHandle`: Full-length paymail handle of the associated entity.
 * - `displayName`: Display name of the associated entity.
 * - `initials`: Up to two characters extracted from the name that can be used as an icon.
 * - `isAnonymous` If `true`, indicates this contact is an anonymous BSV address.
 * - `isInternal`: If `true`, indicates the handle is the paymail for a Tokenized account.
 *   If `false` then `handle` is an external paymail, and supports BSV transfers only.
 * - `isVerified`: Indicates whether the paymail has been verified.
 * - `avatarUrl`: The URL of an image to use as the avatar for the user
 * - `bio`: The bio text from the public profile associated with the handle.
 * - `website`: The website from the public profile associated with the handle.
 * - `...`: all other props supplied to the Avatar component
 *
 * Use like this for example:
 *
 * ```js
 * <Avatar handle={userHandle} className={"avatarImage"} />
 * ```
 *
 * or this with a render prop:
 *
 * ```js
 * <Avatar
 *   handle={userHandle}
 *   render={({src, displayName}) => (
 *     <div className={"avatarComponent"}>
 *       <img src={src}/>
 *       {displayName}
 *     </div>
 *   }
 * />
 * ```
 *
 * @category React Components
 */
export function Avatar({
  handle,
  contact,
  counterparty,
  render = Image,
  renderMissing,
  ...props
}) {
  // Note that the counterparties endpoint doesn’t return the required
  // information to render an avatar, hence the test for the presence
  // of counterpartyType.
  const contactProvided =
    !!contact && !counterparty && !contact?.counterpartyType;
  const fetchedContact = useContact(
    contactProvided ? undefined : handle,
    contactProvided ? undefined : counterparty,
  );
  if (!contactProvided) {
    contact = fetchedContact;
  }
  const src = contact?.avatarUrl;
  const defaultProps = { width: 30, height: 30 };
  if (!contact?.isValid && renderMissing) {
    return (
      <>
        {renderMissing({
          ...defaultProps,
          ...props,
          src,
          ...contact,
          contactIsLoaded: !!contact,
        })}
      </>
    );
  }
  const { address } = counterparty || {};
  return (
    <>
      {render({
        ...defaultProps,
        ...props,
        src,
        ...contact,
        contactIsLoaded: !!contact,
        address,
      })}
    </>
  );
}
